import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';
import { CLUB_MODULE_KEYS, MODULE_REGISTRY, type KnownClubModule } from '@/lib/moduleRegistry';

type DB = SupabaseClient<Database>;

/**
 * The one reader of the club-module resolver (Members phase 5): the SQL functions `club_module_state`
 * and `club_module_states` decide, for a club, a module and a day, whether the module is on, why,
 * until when and where the club stands. Every gate asks through here; nothing else reads
 * `club_affiliation_type.modules`, `location.portal_enabled` or `club_affiliation` dates to decide
 * access (guarded by `src/__tests__/clubModuleGuard.test.ts`). Service role only: pass the admin client.
 */

/** A module the resolver answers for: every club module the database knows and the pseudo-module for sales. */
export type ResolverModule = KnownClubModule | 'sell_memberships';

export const MODULE_REASONS = [
  'legacy_portal_off',
  'not_provisioned',
  'not_onboarded',
  'not_enforced',
  'affiliated',
  'in_grace',
  'never_affiliated',
  'lapsed',
  'ended',
  'not_in_tier',
] as const;
export type ModuleReason = (typeof MODULE_REASONS)[number];

export const CLUB_STANDINGS = ['none', 'current', 'grace', 'lapsed', 'ended', 'legacy'] as const;
export type ClubStandingKind = (typeof CLUB_STANDINGS)[number];

export interface ModuleAnswer {
  on: boolean;
  reason: ModuleReason;
  /** The last day the answer holds (a grace end, the day before enforcement); null when unbounded. */
  until: string | null;
  standing: ClubStandingKind;
}

export type ModuleVerdict = Omit<ModuleAnswer, 'standing'>;

export interface ModuleStates {
  standing: ClubStandingKind;
  modules: Record<ResolverModule, ModuleVerdict>;
}

/**
 * Club keys the registry does not offer (leagues). An answer from a database that predates the key
 * (migration not applied yet) may omit them: that reads as off, so code can deploy before the migration.
 */
const NOT_OFFERED_MODULES: ReadonlySet<string> = new Set(
  MODULE_REGISTRY.filter((m) => m.owner === 'club' && !m.offered).map((m) => m.key),
);

const RESOLVER_MODULES: readonly ResolverModule[] = [
  'events',
  'ladder',
  'club_portal',
  'leagues',
  'streaming',
  'coaching',
  'sell_memberships',
];

/** The reasons that mean "the club is not affiliated for this module": the module is paused. */
const PAUSED_REASONS: ReadonlySet<ModuleReason> = new Set([
  'never_affiliated',
  'lapsed',
  'ended',
  'not_in_tier',
]);

const isReason = (v: unknown): v is ModuleReason =>
  typeof v === 'string' && (MODULE_REASONS as readonly string[]).includes(v);
const isStanding = (v: unknown): v is ClubStandingKind =>
  typeof v === 'string' && (CLUB_STANDINGS as readonly string[]).includes(v);

function verdictOf(raw: unknown): ModuleVerdict | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.on !== 'boolean' || !isReason(r.reason)) return null;
  return { on: r.on, reason: r.reason, until: typeof r.until === 'string' ? r.until : null };
}

/** One module's answer for a club. `on` is a YYYY-MM-DD day; omitted, the resolver uses the root's own day. */
export async function clubModuleState(
  admin: DB,
  locationId: string,
  module: ResolverModule,
  on?: string,
): Promise<ModuleAnswer> {
  const { data, error } = await admin.rpc('club_module_state', {
    p_location: locationId,
    p_module: module,
    ...(on ? { p_on: on } : {}),
  });
  if (error) throw new Error(`clubModuleState failed: ${error.message}`);
  const verdict = verdictOf(data);
  const standing = (data as { standing?: unknown } | null)?.standing;
  if (!verdict || !isStanding(standing)) {
    throw new Error('clubModuleState failed: unexpected resolver payload');
  }
  return { ...verdict, standing };
}

/** Every module's answer for a club on one shared day, plus where the club stands. */
export async function clubModuleStates(
  admin: DB,
  locationId: string,
  on?: string,
): Promise<ModuleStates> {
  const { data, error } = await admin.rpc('club_module_states', {
    p_location: locationId,
    ...(on ? { p_on: on } : {}),
  });
  if (error) throw new Error(`clubModuleStates failed: ${error.message}`);
  const raw = data as { standing?: unknown; modules?: Record<string, unknown> } | null;
  if (!raw || !isStanding(raw.standing) || typeof raw.modules !== 'object' || !raw.modules) {
    throw new Error('clubModuleStates failed: unexpected resolver payload');
  }
  const modules = {} as Record<ResolverModule, ModuleVerdict>;
  for (const m of RESOLVER_MODULES) {
    if (raw.modules[m] === undefined && NOT_OFFERED_MODULES.has(m)) {
      // `not_provisioned` is not a paused reason: callers treat it as plain off.
      modules[m] = { on: false, reason: 'not_provisioned', until: null };
      continue;
    }
    const v = verdictOf(raw.modules[m]);
    if (!v) throw new Error(`clubModuleStates failed: unexpected resolver payload for ${m}`);
    modules[m] = v;
  }
  return { standing: raw.standing, modules };
}

/**
 * The module is off because the club is not affiliated for it (never, lapsed, ended, or the tier
 * leaves it out). `not_onboarded` and `legacy_portal_off` are not paused: they keep today's refusal.
 */
export function isPaused(a: Pick<ModuleAnswer, 'on' | 'reason'>): boolean {
  return !a.on && PAUSED_REASONS.has(a.reason);
}

/** What a club can be told about: the offered club modules and selling memberships. */
const TOLD_MODULES: ReadonlySet<ResolverModule> = new Set<ResolverModule>([
  ...CLUB_MODULE_KEYS,
  'sell_memberships',
]);

/**
 * The modules of one `clubModuleStates` answer that are paused for lack of affiliation (see `isPaused`).
 * A key the registry does not offer yet (leagues) is never listed: the club has no such module to lose.
 */
export function pausedModuleKeys(states: Pick<ModuleStates, 'modules'>): ResolverModule[] {
  return RESOLVER_MODULES.filter((m) => TOLD_MODULES.has(m) && isPaused(states.modules[m]));
}
